/**
 * Storage utility functions for managing authentication tokens and user data
 * - Centralizes localStorage/sessionStorage access to make future changes easier
 * - All functions safely catch storage exceptions (e.g., in private mode)
 */

// Keys for localStorage
const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER_DATA: 'user_data',
  REFRESH_TOKEN: 'refresh_token'
};

/**
 * Save authentication token to storage
 * @param {string} token - The authentication token
 * @param {boolean} rememberMe - Whether to use localStorage (true) or sessionStorage (false)
 */
export const saveAuthToken = (token, rememberMe = false) => {
  try {
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  } catch (error) {
    console.error('Error saving auth token:', error);
  }
};

/**
 * Get authentication token from storage (checks both localStorage and sessionStorage)
 * @returns {string|null} The authentication token or null if not found
 */
export const getAuthToken = () => {
  try {
    // First check localStorage, then sessionStorage
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || 
           sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

// NOTE: These storage helpers intentionally prefer localStorage first so that
// when `rememberMe` is used the persisted credentials are found before session
// credentials. All removal helpers clear both storage locations for safety.

/**
 * Save refresh token to storage
 * @param {string} token - The refresh token
 * @param {boolean} rememberMe - Whether to use localStorage (true) or sessionStorage (false)
 */
export const saveRefreshToken = (token, rememberMe = false) => {
  try {
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  } catch (error) {
    console.error('Error saving refresh token:', error);
  }
};

/**
 * Get refresh token from storage (checks both localStorage and sessionStorage)
 * @returns {string|null} The refresh token or null if not found
 */
export const getRefreshToken = () => {
  try {
    // First check localStorage, then sessionStorage
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || 
           sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  } catch (error) {
    console.error('Error getting refresh token:', error);
    return null;
  }
};

/**
 * Save user data to storage
 * @param {object} userData - The user data object
 * @param {boolean} rememberMe - Whether to use localStorage (true) or sessionStorage (false)
 */
export const saveUserData = (userData, rememberMe = false) => {
  try {
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
  } catch (error) {
    console.error('Error saving user data:', error);
  }
};

/**
 * Get user data from storage (checks both localStorage and sessionStorage)
 * @returns {object|null} The user data object or null if not found
 */
export const getUserData = () => {
  try {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA) || 
                    sessionStorage.getItem(STORAGE_KEYS.USER_DATA);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

/**
 * Remove authentication token from both localStorage and sessionStorage
 */
export const removeAuthToken = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  } catch (error) {
    console.error('Error removing auth token:', error);
  }
};

/**
 * Remove refresh token from both localStorage and sessionStorage
 */
export const removeRefreshToken = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  } catch (error) {
    console.error('Error removing refresh token:', error);
  }
};

/**
 * Remove user data from both localStorage and sessionStorage
 */
export const removeUserData = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
  } catch (error) {
    console.error('Error removing user data:', error);
  }
};

/**
 * Clear all authentication related data from localStorage
 */
export const clearAuthData = () => {
  removeAuthToken();
  removeRefreshToken();
  removeUserData();
};

/**
 * Check if user is authenticated (has valid token)
 * @returns {boolean} True if user has authentication token
 */
export const isAuthenticated = () => {
  const token = getAuthToken();
  return !!token;
};
